/**
 * @fileoverview The To-dos tray on a plant's card and a bed's card (D50; B136 and B158): the gardener's own to-dos about this plant or
 * bed, found again where they were put. CardTiles draws it under the tiles when the To-dos tile is open (the tile carries the count
 * and the card holds the one open tray), so this is the list alone: no heading, no fold.
 *
 * Open, each to-do is one compact row, a thumb and a half tall: the tick circle, the title (wrapping), its when pill at the right
 * and a chevron. The circle ticks it (a plant's tick is written in its journal; a bed's in the bed's); a tap on the words opens the
 * edit sheet. No reason line and no foot: Postpone and Skip it live on Today; a planting to-do's gentle line (E23, the server's
 * tip: "Grow suggests planting mid to late February") shows under its title, and its circle asks which bed a Seed vault plant went
 * into (the server's askBed, as a planting job's). Last, the way to add one: on a plant's card "Add a to-do"; on a bed's card the
 * "To-do" and "Plant" buttons side by side (E25, AddAndPlantButtons: Plant something opens with the bed chosen). A to-do carried
 * from an earlier day opens its edit sheet with its pill's "Since ..." (E25).
 *
 * The list is the server's (the brief's todo.own, newest first: every open own to-do, any day, done ones struck through on the day
 * they were ticked); this keeps the ones about this plant or bed and draws them. The pill's words and tone are the server's
 * (WHEN_TONE). Nothing shows without a brief (no postcode: nothing can be added) or for an archived plant or bed.
 *
 * Where: CardTiles (the To-dos tray of the plant card and the bed card).
 * API: none of its own: the brief (useTodayBrief, the one read every page shares), the marks (useTodoMarks: POST /todo/marks) and
 * the sheet's writes (useOwnTodoSheet).
 * Words (en/es): todo.own.add and todo.own.plant.button (AddAndPlantButtons), todo.own.open (aria), todo.tick (aria); the to-dos'
 * own words are the server's.
 *
 * @module own-todos/OwnTodosRow
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import { FailureText } from '@/shared/components/ServerFailure';
import type { OwnTodoItem } from '@/shared/api/todayBrief';
import { useTodoMarks } from '@/features/today/lib/useTodoMarks';
import { TickCircle, WHEN_TONE } from '@/features/today/components/JobCard';
import { useOwnTodoSheet } from './useOwnTodoSheet';
import { AddTodoRow } from './AddTodoRow';
import { AddAndPlantButtons } from './AddAndPlantButtons';

/** The row's props. */
interface OwnTodosRowProps {
  /** What the card is: a plant or a bed. */
  about: 'plant' | 'bed';
  /** The plant (plant_obj_id) or the bed (location id). */
  id: number;
  /** Its name, for the sheet's title ("A to-do for Cherokee Purple tomato"). */
  name: string;
  /** An archived plant or bed: nothing to add, and no row. */
  archived?: boolean;
}

/**
 * The To-dos tray's list for one plant or bed.
 *
 * @param props - OwnTodosRowProps.
 */
export const OwnTodosRow: React.FC<OwnTodosRowProps> = ({ about, id, name, archived = false }) => {
  const { reactTranslate: t } = useTranslation();
  const { brief, queryKey, ask } = useTodayBrief();
  const marks = useTodoMarks(queryKey, ask);
  const sheet = useOwnTodoSheet(queryKey, ask, marks, brief?.date ?? '');
  /** This plant's or bed's own to-dos, in the server's order. */
  const items = React.useMemo((): OwnTodoItem[] => (brief?.todo.own ?? []).filter((item) => (
    about === 'plant' ? item.about === 'plant' && item.plantObjId === id : item.about === 'bed' && item.locationId === id
  )), [about, brief, id]);

  if (!brief || archived) return null;
  /** Opens the sheet to add one about this plant or bed. */
  const add = (): void => sheet.openAdd({ about, id, name });
  /**
   * The way to add one: on a bed's card, "Add a to-do" and "Plant something" (E25); on a plant's, "Add a to-do".
   *
   * @param className - Its place on the card.
   */
  const adder = (className: string): React.ReactNode => (about === 'bed'
    ? <AddAndPlantButtons variant="row" onAdd={add} onPlant={() => sheet.openPlant({ locationId: id })} className={className} />
    : <AddTodoRow onClick={add} className={className} />);

  /**
   * One to-do's compact row.
   *
   * @param item - The to-do.
   */
  const row = (item: OwnTodoItem): React.ReactNode => {
    const done = marks.pending[item.key]?.done ?? item.done;
    const failed = marks.failed?.key === item.key ? marks.failed.words : null;
    return (
      <li key={item.key} className="border-t first:border-t-0">
        <div className="flex min-h-12 items-center gap-1">
          {/* The circle ticks it; a thumb's 44px around the circle that shows. */}
          <button
            type="button"
            onClick={() => { void marks.mark(null, { action: done ? 'undone' : 'done', key: item.key }, item.askBed); }}
            disabled={!item.tickable || item.skipped || marks.busyKey === item.key}
            aria-pressed={done}
            aria-label={t('todo.tick', { job: item.title })}
            className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            <TickCircle done={done && !item.skipped} />
          </button>
          {/* The words open the edit sheet. */}
          <button
            type="button"
            onClick={() => sheet.openEdit({ id: item.id, key: item.key, title: item.title, about: item.about, job: item.job, planned: item.planned, since: item.when.code === 'since' ? item.when.text : null })}
            aria-label={t('todo.own.open', { title: item.title })}
            className="flex min-h-12 min-w-0 flex-1 items-center gap-2 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className={cn('min-w-0 flex-1 text-[0.9375rem] font-semibold leading-snug', (done || item.skipped) && 'text-muted-foreground line-through')}>{item.title}</span>
            <span className={cn('shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[0.8125rem] font-semibold leading-none', WHEN_TONE[item.when.tone], (done || item.skipped) && 'opacity-60')}>
              {item.when.text}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>
        {/* The server sends no line once ticked or skipped; a tick on its way hides it at once. */}
        {item.tip && !done && <p className="pb-2 pl-10 text-sm leading-snug text-muted-foreground">{item.tip}</p>}
        {failed && <p className="pb-2 pl-10 text-xs text-destructive" role="alert"><FailureText text={failed} /></p>}
      </li>
    );
  };

  return (
    <>
      <div className="px-4 pb-1">
        {items.length > 0 && <ul>{items.map(row)}</ul>}
        {adder(items.length > 0 ? 'border-t' : '')}
      </div>
      {sheet.element}
    </>
  );
};
